import React, { useState } from 'react';
import { 
  useInsufficientKeywordsCampaigns, 
  useCampaignKeywordDetails,
  useGenerateKeywordRecommendations,
  useApplyKeywordRecommendation
} from '../../lib/api-extensions/admin-queries';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../hooks/useToast';

// UI Components
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectGroup, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { getMatchTypePillClass, formatMatchType } from '@/lib/match-type';

// Custom components - need to define these since they aren't in the UI library
const Text: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => <p className={`text-sm ${className}`}>{children}</p>;
const Title: React.FC<{ children: React.ReactNode; className?: string; level?: 1 | 2 | 3 | 4 | 5 }> = ({ children, className = '', level = 2 }) => {
  const Component = `h${level}`;
  const sizeClasses = {
    1: 'text-2xl font-bold',
    2: 'text-xl font-semibold',
    3: 'text-lg font-semibold',
    4: 'text-base font-medium',
    5: 'text-sm font-medium',
  };
  // Using a dynamic tag name requires explicit typing in React
  const Tag = Component as keyof JSX.IntrinsicElements;
  return <Tag className={`${sizeClasses[level as keyof typeof sizeClasses]} ${className}`}>{children}</Tag>;
};
const Group: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => <div className={`flex gap-2 items-center ${className}`}>{children}</div>;
// Removed unused Stack component

// Removed unused interface
// interface CampaignWithKeywordCount {
// id: number;
// name: string;
// status: string;
// keywordCount: number;
// adCount: number;
// }

interface KeywordSuggestion {
  text: string;
  matchType: string;
  searchVolume?: number;
  competitionLevel?: string;
  selected?: boolean;
}

export const AdminKeywordManagement: React.FC = () => {
  const [threshold, setThreshold] = useState(10);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [keywordSuggestions, setKeywordSuggestions] = useState<KeywordSuggestion[]>([]);
  const [recommendationId, setRecommendationId] = useState<number | null>(null);
  
  const { data: campaignsData, isLoading: campaignsLoading } = useInsufficientKeywordsCampaigns(threshold);
  const { data: campaignDetails, isLoading: detailsLoading } = useCampaignKeywordDetails(selectedCampaign || 0);
  
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const generateKeywordsMutation = useGenerateKeywordRecommendations();
  const applyKeywordsMutation = useApplyKeywordRecommendation();
  
  const handleGenerateKeywords = async (campaignId: number) => {
    try {
      const result = await generateKeywordsMutation.mutateAsync({campaignId, count: 20});

      // Make sure all keywords start as selected
      const keywordsWithSelection = result.recommendations.map((kw: KeywordSuggestion) => ({...kw, selected: true}));

      setKeywordSuggestions(keywordsWithSelection);
      setRecommendationId(result.recommendation_id);

      toast({
        title: 'Keyword Suggestions Generated',
        description: `Generated ${result.recommendations.length} keyword suggestions for ${result.campaign_name}`,
        variant: 'default',
      });
    } catch (error) {
      console.error("Error generating keywords:", error);
      toast({
        title: 'Error Generating Keywords',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };
  
  const handleApplyKeywords = async () => {
    if (!recommendationId) {
      console.error("No recommendation ID found");
      toast({
        title: 'Error',
        description: 'No recommendation ID found',
        variant: 'destructive',
      });
      return;
    }
    
    // Filter only the selected keywords
    const selectedKeywords = keywordSuggestions
      .filter(kw => kw.selected)
      .map(kw => ({ text: kw.text }));
      
    if (selectedKeywords.length === 0) {
      console.error("No keywords selected");
      toast({
        title: 'Error',
        description: 'No keywords selected',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      const result = await applyKeywordsMutation.mutateAsync({
        recommendationId,
        keywords: selectedKeywords,
        matchType: 'BROAD', // Default match type
      });
      
      toast({
        title: 'Keywords Added',
        description: `Successfully added ${result.keywords_added} keywords to the campaign`,
        variant: 'default',
      });
      
      // Reset the keyword suggestions after successful application
      setKeywordSuggestions([]);
      setRecommendationId(null);
      
      // Refresh data for the selected campaign if one is selected
      if (selectedCampaign) {
        queryClient.invalidateQueries({
          queryKey: ['admin', 'campaign-keyword-details', selectedCampaign],
        });
      }
      
      // Refresh the list of campaigns with insufficient keywords
      queryClient.invalidateQueries({
        queryKey: ['admin', 'insufficient-keywords-campaigns', threshold],
      });

    } catch (error) {
      toast({
        title: 'Error Adding Keywords',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };
  
  const toggleKeywordSelection = (index: number) => {
    setKeywordSuggestions(prev => {
      const newSuggestions = prev.map((kw, i) => {
        if (i === index) {
          const newValue = !kw.selected;
          return {...kw, selected: newValue};
        }
        return kw;
      });
      
      return newSuggestions;
    });
  };
  
  return (
    <div className="admin-keyword-management p-6">
      <Title className="mb-6">Campaign Keyword Management</Title>
      
      <Card className="mb-6 p-4">
        <Title level={3} className="mb-4">Campaigns with Insufficient Keywords</Title>
        <Group className="mb-4">
          <Text>Minimum keywords:</Text>
          <Select onValueChange={(value) => setThreshold(parseInt(value))} value={threshold.toString()}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Select threshold" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {[5, 10, 15, 20, 30].map(value => (
                  <SelectItem key={value} value={value.toString()}>
                    {value}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Group>
        
        {campaignsLoading ? (
          <div className="flex justify-center my-8">
            <Spinner size="lg" />
          </div>
        ) : !campaignsData?.items?.length ? (
          <Text>No campaigns found with fewer than {threshold} keywords.</Text>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Keywords</TableHead>
                <TableHead>Ads</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaignsData?.items.map((campaign: any) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>
                    <Badge variant={campaign.status === 'active' ? 'success' : 'default'}>
                      {campaign.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{campaign.keyword_count}</TableCell>
                  <TableCell>{campaign.ad_count}</TableCell>
                  <TableCell>
                    <Group>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedCampaign(campaign.id)}
                      >
                        View Details
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleGenerateKeywords(campaign.id)}
                        disabled={generateKeywordsMutation.isPending}
                      >
                        Generate Keywords
                      </Button>
                    </Group>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      
      {selectedCampaign && campaignDetails && (
        <Card className="mb-6 p-4">
          <Title level={3} className="mb-4">Campaign Details: {campaignDetails.name}</Title>
          <Group className="mb-4">
            <Badge variant="outline">Ads: {campaignDetails.ad_count}</Badge>
            <Badge variant="outline">Keywords: {campaignDetails.total_keyword_count}</Badge>
          </Group>
          
          <Title level={4} className="mb-2">Ads and Keywords</Title>
          {detailsLoading ? (
            <div className="flex justify-center my-8">
              <Spinner size="lg" />
            </div>
          ) : (
            campaignDetails.ads.map((ad: any) => (
              <Card key={ad.id} className="mb-2 p-3">
                <Title level={5}>{ad.headline}</Title>
                <Text className="text-sm text-muted-foreground mb-2">{ad.url}</Text>
                <Text className="mb-2">Keywords: {ad.keyword_count}</Text>
                
                {ad.keyword_count > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {ad.keywords.map((keyword: any) => (
                      <Badge key={keyword.id} variant="secondary" className="mr-1 mb-1 inline-flex items-center gap-1.5">
                        {keyword.text}
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getMatchTypePillClass(keyword.matchType ?? keyword.match_type)}`}>
                          {formatMatchType(keyword.matchType ?? keyword.match_type)}
                        </span>
                      </Badge>
                    ))}
                  </div>
                )}
              </Card>
            ))
          )}
          
          <div className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setSelectedCampaign(null)}
            >
              Back to Campaigns
            </Button>
          </div>
        </Card>
      )}
      
      {keywordSuggestions.length > 0 && (
        <Card className="p-4">
          <Title level={3} className="mb-4">Keyword Suggestions</Title>
          <Text className="mb-4">
            Select keywords to add to the campaign. These keywords will be added to the appropriate ads.
          </Text>
          
          <div className="mb-4">
            {keywordSuggestions.map((keyword, index) => (
              <div key={index} className="flex items-center mb-2">
                <Checkbox
                  id={`keyword-${index}`}
                  checked={keyword.selected || false}
                  onCheckedChange={() => toggleKeywordSelection(index)}
                  className="mr-2"
                />
                <Text>{keyword.text}</Text>
                <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getMatchTypePillClass(keyword.matchType)}`}>
                  {formatMatchType(keyword.matchType)}
                </span>
                {keyword.searchVolume && (
                  <Badge variant="outline" className="ml-2">
                    Volume: {keyword.searchVolume}
                  </Badge>
                )}
                {keyword.competitionLevel && (
                  <Badge variant="outline" className="ml-2">
                    Competition: {keyword.competitionLevel}
                  </Badge>
                )}
              </div>
            ))}
          </div>
          
          <Group>
            {/* Debug info to show why the button might be disabled */}
            <div className="text-xs text-gray-500 mb-2">
              <div>Selected: {keywordSuggestions.filter(kw => kw.selected).length} of {keywordSuggestions.length}</div>
              <div>Has Selected: {keywordSuggestions.some(kw => kw.selected) ? 'Yes' : 'No'}</div>
              <div>Is Pending: {applyKeywordsMutation.isPending ? 'Yes' : 'No'}</div>
              <div>Button Disabled: {(!keywordSuggestions.some(kw => kw.selected) || applyKeywordsMutation.isPending) ? 'Yes' : 'No'}</div>
            </div>
            
            <Button
              onClick={handleApplyKeywords}
              disabled={!keywordSuggestions.some(kw => kw.selected) || applyKeywordsMutation.isPending}
            >
              {applyKeywordsMutation.isPending ? 'Applying...' : 'Apply Selected Keywords'}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setKeywordSuggestions([]);
                setRecommendationId(null);
              }}
              disabled={applyKeywordsMutation.isPending}
            >
              Cancel
            </Button>
          </Group>
        </Card>
      )}
    </div>
  );
};